/**
 * AssignToUserDialog
 *
 * Coordinator/Manager hands off a form template to a specific staff user
 * (target_entity='registered_user' templates only — member-attached forms
 * keep their existing enrollment-bound flow). Creates a form_instance with
 * `assigned_to_user_id` set; the assigned user picks it up from the
 * "My Forms" inbox.
 *
 * Honors X-CBO-ID (via authFetchJson) and pulls the staff pool from the
 * Phase-2b `?exclude_admins=true` endpoint so Trust ops_admins don't
 * pollute the picker.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, UserCheck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { authFetchJson } from '@/lib/authFetch';
import { formsAPI, FormTemplate } from '@/services/api/forms';

interface AssignToUserDialogProps {
  open: boolean;
  template: FormTemplate | null;
  onClose: () => void;
  onAssigned?: (instanceId: number) => void;
}

interface StaffOption {
  user_id: number;
  name: string;
  email?: string;
  role?: string;
}

export const AssignToUserDialog: React.FC<AssignToUserDialogProps> = ({
  open,
  template,
  onClose,
  onAssigned,
}) => {
  const [staff, setStaff] = useState<StaffOption[] | null>(null);
  const [staffError, setStaffError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState<string>('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedUserId(null);
    setDueDate('');
    setSearch('');
    setSubmitError(null);
    const cboId = Number(localStorage.getItem('active_cbo_id') || 0);
    if (!cboId) {
      setStaffError('No active organization selected.');
      return;
    }
    setStaff(null);
    setStaffError(null);
    authFetchJson<any[]>(`/api/organization-setup/${cboId}/staff?exclude_admins=true`)
      .then((rows) => {
        setStaff(
          (rows || [])
            .filter((r) => r.is_active !== false)
            .map((r) => ({
              user_id: r.user_id ?? r.id,
              name: r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email,
              email: r.email,
              role: r.role || r.role_display_name,
            }))
            .filter((s) => s.user_id),
        );
      })
      .catch((e: any) => setStaffError(e?.message || 'Failed to load staff list'));
  }, [open]);

  const filtered = useMemo(() => {
    if (!staff) return [];
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.email && s.email.toLowerCase().includes(q)) ||
        (s.role && s.role.toLowerCase().includes(q)),
    );
  }, [staff, search]);

  const handleAssign = async () => {
    if (!template || !selectedUserId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await formsAPI.createFormInstance({
        form_template_id: template.id,
        assigned_to_user_id: selectedUserId,
        ...(dueDate ? { due_date: dueDate } : {}),
      });
      onAssigned?.(created.id);
      onClose();
    } catch (e: any) {
      setSubmitError(e?.response?.data?.detail || e?.message || 'Failed to assign form');
    } finally {
      setSubmitting(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-blue-600" />
            Assign form
          </DialogTitle>
          <DialogDescription>
            Assign <strong>{template.name}</strong> to a staff member. They&apos;ll see it in
            their <em>My Forms</em> inbox.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Find staff</label>
            <Input
              placeholder="Search by name, email, or role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {staffError && (
            <div className="text-sm bg-red-50 border border-red-200 text-red-900 rounded-md p-3 inline-flex gap-2 items-start">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              {staffError}
            </div>
          )}
          {!staff && !staffError && (
            <div className="text-xs text-gray-500 inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading staff…
            </div>
          )}
          {staff && (
            <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-xs text-gray-500 text-center">
                  No staff match your search.
                </div>
              ) : (
                filtered.map((s) => {
                  const isSelected = selectedUserId === s.user_id;
                  return (
                    <button
                      key={s.user_id}
                      type="button"
                      onClick={() => setSelectedUserId(s.user_id)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        isSelected
                          ? 'bg-blue-50 dark:bg-blue-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <div className="flex justify-between items-center gap-2">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{s.name}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {s.role || '—'}
                            {s.email && ` · ${s.email}`}
                          </div>
                        </div>
                        {isSelected && <span className="text-xs text-blue-600">Selected</span>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 block mb-1">Due date (optional)</label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>

          {submitError && (
            <div className="text-sm bg-red-50 border border-red-200 text-red-900 rounded-md p-3 inline-flex gap-2 items-start">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              {submitError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!selectedUserId || submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Assigning…
              </>
            ) : (
              'Assign form'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignToUserDialog;
